import { Button, Input, Typography } from 'antd';
import {
	TwitterShareButton,
	LinkedinShareButton,
	TelegramShareButton,
	FacebookShareButton,
} from 'react-share';
import React, { FC, ElementType, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SettingsCard } from './SettingsCard';
import { Popover } from 'componets/ui';

import { useAppSelector, useCopied } from 'hooks';

import { ReactComponent as StarIcon } from 'assets/icons/star.svg';
import { ReactComponent as FacebookIcon } from 'assets/icons/facebook.svg';
import { ReactComponent as TelegramIcon } from 'assets/icons/telegram.svg';
import { ReactComponent as TwitterIcon } from 'assets/icons/twitter.svg';
import { ReactComponent as LinkedinIcon } from 'assets/icons/linkedin.svg';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';

import Utils from 'utils/utils';
import { VerificationStatus, Paths } from 'utils/constants';
import { language } from 'utils';

import s from './index.module.scss';

export const SettingsReferralCard = () => {
	const user = useAppSelector((state) => state.user.entity);

	const { setIsCopied } = useCopied();

	const { t } = useTranslation();

	const isLithuanian = language === 'lt';

	const referralLink = Utils.getLinkToWP(
		`${isLithuanian ? '/lt' : ''}/ref/${user.referralCode}`,
		true,
	);

	const isVerified = user.verificationStatus === VerificationStatus.VERIFIED;

	return (
		<SettingsCard
			title={
				<div className={s.loyaltyTitle}>
					{t('inviteFriendProgramText')} <StarIcon />
				</div>
			}
			className="loyalty"
		>
			<div className={s.referralRow}>
				<Typography.Text className={s.text}>{t('inviteFriendProgramInfoText')}</Typography.Text>
			</div>

			{isVerified && user.taxResidence ? (
				<div className={s.row}>
					<div className={s.col}>
						<Typography.Text className={s.text}>{t('shareLinkText')}</Typography.Text>
						<div className={s.inputWrapper}>
							<Input className={s.input} disabled value={referralLink} />
							<div className={s.copyWrapper}>
								<Button
									className={s.copyLink}
									onClick={() => {
										setIsCopied(true);

										Utils.copyText(referralLink);
									}}
									type="link"
									icon={<CopyIcon className="copyIcon" />}
								/>
							</div>
						</div>
					</div>

					<div className={s.share}>
						<Typography.Text className={s.text}>{t('shareViaText')}</Typography.Text>

						<div className={s.shareLinks}>
							<ShareLink url={referralLink} Component={FacebookShareButton}>
								<FacebookIcon />
							</ShareLink>

							<ShareLink url={referralLink} Component={LinkedinShareButton}>
								<LinkedinIcon />
							</ShareLink>

							<ShareLink url={referralLink} Component={TelegramShareButton}>
								<TelegramIcon />
							</ShareLink>

							<ShareLink url={referralLink} Component={TwitterShareButton}>
								<TwitterIcon />
							</ShareLink>
						</div>
					</div>
				</div>
			) : (
				''
			)}

			{!isVerified && !user.taxResidence && (
				<div className={s.referralRowText}>
					<Typography.Text className={s.text}>
						{t('inviteFriendVerifyAndTaxResidenceText')}
					</Typography.Text>
				</div>
			)}

			{!isVerified && user.taxResidence && (
				<div className={s.referralRowText}>
					<Typography.Text className={s.text}>
						<Trans
							i18nKey="inviteFriendVerifyIdentityText"
							components={{ l: <Link to={Paths.VERIFY_IDENTITY} /> }}
						/>
					</Typography.Text>
				</div>
			)}

			{!user.taxResidence && isVerified && (
				<div className={s.referralRowText}>
					<Typography.Text className={s.text}>
						<Trans
							i18nKey="inviteFriendFillTaxResidenceText"
							components={{ l: <Link to={Paths.RESIDENCY} /> }}
						/>
					</Typography.Text>
				</div>
			)}

			<Typography.Text type="secondary" className={s.readMore}>
				<a
					href={`https://crowdpear.com/${
						isLithuanian ? 'lt/duk-category/pakviesk-drauga/' : 'help-category/invite-a-friend/'
					}`}
					className={s.link}
					target="_blank"
					rel="noreferrer"
				>
					{t('readMoreInviteFriendText')}
				</a>
			</Typography.Text>
		</SettingsCard>
	);
};

const ShareLink: FC<{ Component: ElementType; url: string; children: ReactNode }> = ({
	Component,
	children,
	url,
}) => {
	const { t } = useTranslation();
	return (
		<Component url={url}>
			<Popover content={t('shareText')} trigger="hover" isButton>
				{children}
			</Popover>
		</Component>
	);
};
